/**
 * Bottone «Condividi» di uno studio della Rete Certificata: aperto, mostra in
 * linea le due scelte «Copia dati e link» e «Invia su WhatsApp». Entrambe
 * portano i dati dello studio (nome, indirizzo, telefono, email, pratiche)
 * piu' il link profondo `/rete-certificata?studio=<id>` che riapre la mappa
 * sulla sua scheda. Le scelte stanno in linea (non a tendina) perche' nel popup
 * Leaflet un menu sovrapposto verrebbe tagliato. Usato nell'elenco (variante
 * `card`, tema chiaro e scuro) e nel popup (variante `popup`, fondo bianco).
 */
import React, { useState } from 'react';
import { Check, Copy, MessageCircle, Share2, X } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { copyToClipboard } from './support/shareTicket';
import { testoStudio, urlStudio, whatsappHref, type StudioCondivisibile } from '../utils/condividiStudio';

export interface CondividiStudioProps {
  studio: StudioCondivisibile;
  variant?: 'card' | 'popup';
  className?: string;
}

/** Toast se c'e' il provider; senza (test, montaggi isolati) resta la conferma sul bottone. */
const useToastIfAvailable = (): ReturnType<typeof useToast> | undefined => {
  try {
    return useToast();
  } catch {
    return undefined;
  }
};

const CondividiStudio: React.FC<CondividiStudioProps> = ({ studio, variant = 'card', className = '' }) => {
  const toast = useToastIfAvailable();
  const [aperto, setAperto] = useState(false);
  const [copiato, setCopiato] = useState(false);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const url = urlStudio(origin, studio.id);
  const testo = testoStudio(studio, url);
  const popup = variant === 'popup';

  const copia = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await copyToClipboard(testo);
    if (!ok) {
      toast?.error('Copia non riuscita: usa WhatsApp oppure seleziona i dati a mano');
      return;
    }
    setCopiato(true);
    window.setTimeout(() => setCopiato(false), 2000);
    toast?.success('Dati e link dello studio copiati');
  };

  const base =
    'inline-flex items-center justify-center gap-1.5 min-h-[36px] px-3 rounded-lg text-xs font-semibold transition-colors ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-1';
  const neutro = popup
    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600';
  const verde = popup
    ? 'bg-green-100 text-green-700'
    : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
  const hint = popup ? 'text-gray-500' : 'text-gray-500 dark:text-gray-400';

  if (!aperto) {
    return (
      <div className={className}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setAperto(true); }}
          className={`${base} w-full ${neutro}`}
          aria-expanded={false}
          title="Condividi i dati di questo studio"
        >
          <Share2 className="w-3.5 h-3.5" aria-hidden />
          <span>Condividi</span>
        </button>
      </div>
    );
  }

  return (
    <div className={className} role="group" aria-label={`Condividi ${studio.businessName}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className={`text-xs ${hint}`}>Condividi i dati di questo studio</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setAperto(false); }}
          className={`p-1 rounded ${hint} hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500`}
          aria-label="Chiudi condivisione"
        >
          <X className="w-3.5 h-3.5" aria-hidden />
        </button>
      </div>
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={copia}
          className={`${base} flex-1 ${copiato ? verde : neutro}`}
          aria-live="polite"
          title="Copia negli appunti dati e link dello studio"
        >
          {copiato ? <Check className="w-3.5 h-3.5" aria-hidden /> : <Copy className="w-3.5 h-3.5" aria-hidden />}
          <span>{copiato ? 'Copiato' : 'Copia dati e link'}</span>
        </button>
        <a
          href={whatsappHref(testo)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={`${base} flex-1 bg-[#25D366] hover:bg-[#1ebe5b]`}
          style={{ color: '#fff' }} /* `.leaflet-container a` colorerebbe il testo di blu nel popup */
          title="Invia dati e link dello studio a un contatto WhatsApp"
        >
          <MessageCircle className="w-3.5 h-3.5" aria-hidden />
          <span>Invia su WhatsApp</span>
        </a>
      </div>
    </div>
  );
};

export default CondividiStudio;
